/**
 * @fileoverview Which section weeks count toward a student's certificate
 * (#5307). Live attendance a section leader or staff member verified counts on
 * its own. Any week that rests on a recording, or on a Super Section join
 * nobody verified, also needs that week's catch-up exercise: a short SimTeach
 * puzzle (the teach-back, recorded server-side in the puzzle summary) or a
 * short check on the section's problem (graded and recorded server-side by the
 * attendance callable), whichever the schedule sets, or a credited live Teach
 * it back (#5270, recorded server-side in the learner's summary). A small
 * section student may count at most {@link MAX_CATCH_UP_WEEKS} weeks by
 * recording; Super Section recordings have no cap. The functions certificate
 * check and the webapp (the certificate page and the roadmap's section items)
 * all call {@link computeSectionWeekCredit}, so they can never disagree.
 */
import {
  SectionScheduleDocumentSchema,
  type CatchUpCheck,
  type SectionScheduleDocument,
} from '../firebase/documents/courseSectionSchedule';

/**
 * Most weeks per course a student may count by watching a small-section
 * recording and completing the exercise. Super Section recordings do not use
 * this allowance.
 */
export const MAX_CATCH_UP_WEEKS = 2;

/**
 * How one section week stands for a student's certificate:
 * - `live`: live attendance in a small section, a makeup, or a staff entry.
 * - `superSectionLive`: a Super Section join in a week with no exercise set.
 * - `superSection`: a Super Section join or recording, plus the exercise.
 * - `catchUp`: a small-section recording plus the exercise, within
 *   {@link MAX_CATCH_UP_WEEKS}.
 * - `needsExercise`: a Super Section join or a recording whose week has an
 *   exercise the student has not completed.
 * - `needsRecording`: the exercise is done, but the student neither attended
 *   nor marked the recording watched.
 * - `noExercise`: a recording in a week with no exercise set, which can
 *   never count.
 * - `overCatchUpLimit`: a catch-up past {@link MAX_CATCH_UP_WEEKS}.
 * - `absent`: nothing counts, such as a week marked not attended.
 */
export type SectionWeekCreditStatus =
  | 'live'
  | 'superSectionLive'
  | 'superSection'
  | 'catchUp'
  | 'needsExercise'
  | 'needsRecording'
  | 'noExercise'
  | 'overCatchUpLimit'
  | 'absent';

/** The statuses that count the week toward the certificate. */
const CREDITED_STATUSES: ReadonlySet<SectionWeekCreditStatus> = new Set([
  'live',
  'superSectionLive',
  'superSection',
  'catchUp',
]);

/** Everything {@link computeSectionWeekCredit} reads, all for one student. */
export interface SectionWeekCreditInput {
  /** `sectionAttendance`: course week key to whether they attended live. */
  sectionAttendance: Readonly<Record<string, boolean | undefined>>;
  /**
   * `sectionAttendanceIds`: course week key to the sections attended live. A
   * week with attendance but no ids is a staff entry.
   */
  sectionAttendanceIds: Readonly<Record<string, readonly string[] | undefined>>;
  /** `sectionRecordingAttendance`: week key to whether a recording was marked watched. */
  sectionRecordingAttendance: Readonly<Record<string, boolean | undefined>>;
  /**
   * `sectionRecordingAttendanceIds`: week key to the section whose recording
   * was marked. A marked week without ids predates this document, when only
   * Super Section students could mark recordings, so it counts as a Super
   * Section recording.
   */
  sectionRecordingAttendanceIds: Readonly<
    Record<string, readonly string[] | undefined>
  >;
  /** The course's `superSectionIds`. */
  superSectionIds: readonly string[];
  /**
   * Zero-based weeks with a catch-up exercise: the ones the schedule sets,
   * plus any week whose live Teach it back the student was credited for.
   */
  exerciseWeeks: readonly number[];
  /**
   * Zero-based weeks whose exercise the student completed, in any kind: a
   * teach-back done before a week switched to a check still counts, and so
   * does a credited live Teach it back.
   */
  completedExerciseWeeks: readonly number[];
}

/** One week's standing. */
export interface SectionWeekCreditEntry {
  /** The zero-based course week. */
  weekIndex: number;
  /** How the week stands. */
  status: SectionWeekCreditStatus;
  /** Whether the week counts toward the certificate. */
  isCredited: boolean;
}

/** The result of {@link computeSectionWeekCredit}. */
export interface SectionWeekCredit {
  /**
   * Week key to whether the week counts: the section attendance map the
   * certificate requirement check reads in place of raw `sectionAttendance`.
   */
  creditedAttendance: Record<string, boolean>;
  /** Every week with a live, recording, or exercise signal, ascending. */
  weeks: SectionWeekCreditEntry[];
  /** The weeks counted as catch-ups, ascending. */
  catchUpWeeks: number[];
  /** Catch-ups the student may still count. */
  catchUpsRemaining: number;
}

/**
 * Parses a section week key: a canonical non-negative integer string.
 *
 * @param key - A key of one of the per-week attendance documents.
 * @return The zero-based week, or `null` for any other key.
 * @throws Never.
 */
function parseWeekKey(key: string): number | null {
  if (!/^(0|[1-9]\d*)$/.test(key)) {
    return null;
  }
  return Number(key);
}

/**
 * The teach-back puzzle the schedule sets for one week.
 *
 * @param params.schedule - The course's section schedule; `undefined` while
 *     it loads or when the course has none, which sets no teach-back.
 * @param params.weekIndex - The zero-based course week.
 * @return The puzzle id, or `null` when the week has no teach-back.
 * @throws Never.
 */
export function getTeachBackPuzzleId({
  schedule,
  weekIndex,
}: {
  schedule: SectionScheduleDocument | undefined;
  weekIndex: number;
}): string | null {
  const byWeek = schedule?.teachBackByWeek;
  if (!byWeek || !Number.isInteger(weekIndex) || weekIndex < 0) {
    return null;
  }
  return byWeek[weekIndex]?.puzzleId ?? null;
}

/** The catch-up exercise a week uses. */
export type CatchUpExercise =
  | {
      kind: 'teachBack';
      /** The puzzle's id in `course/{courseId}/simTeachPuzzles`. */
      puzzleId: string;
    }
  | {
      kind: 'check';
      /** The week's questions. */
      check: CatchUpCheck;
    };

/**
 * The catch-up exercise the schedule sets for one week. With
 * `catchUpExerciseByWeek` present, its entry picks the kind, and the week has
 * no exercise when that kind is not authored (`teachBackByWeek` or
 * `catchUpCheckByWeek`). Without it, a week with a teach-back puzzle uses the
 * teach-back, so schedules written before the field keep working.
 *
 * @param params.schedule - The course's section schedule; `undefined` while
 *     it loads or when the course has none, which sets no exercise.
 * @param params.weekIndex - The zero-based course week.
 * @return The exercise, or `null` when the week has none.
 * @throws Never.
 */
export function getCatchUpExercise({
  schedule,
  weekIndex,
}: {
  schedule: SectionScheduleDocument | undefined;
  weekIndex: number;
}): CatchUpExercise | null {
  if (!schedule || !Number.isInteger(weekIndex) || weekIndex < 0) {
    return null;
  }
  const puzzleId = getTeachBackPuzzleId({schedule, weekIndex});
  const kind =
    schedule.catchUpExerciseByWeek === undefined
      ? puzzleId === null
        ? null
        : 'teachBack'
      : (schedule.catchUpExerciseByWeek[weekIndex] ?? null);
  if (kind === 'teachBack') {
    return puzzleId === null ? null : {kind, puzzleId};
  }
  if (kind === 'check') {
    const check = schedule.catchUpCheckByWeek?.[weekIndex] ?? null;
    return check === null ? null : {kind, check};
  }
  return null;
}

/**
 * The zero-based weeks whose catch-up exercise the schedule sets.
 *
 * @param schedule - The course's section schedule; `undefined` sets none.
 * @return The weeks, ascending.
 * @throws Never.
 */
export function getExerciseWeeks(
  schedule: SectionScheduleDocument | undefined,
): number[] {
  const weekCount = Math.max(
    schedule?.teachBackByWeek?.length ?? 0,
    schedule?.catchUpExerciseByWeek?.length ?? 0,
  );
  return Array.from({length: weekCount}, (_, weekIndex) => weekIndex).filter(
    weekIndex => getCatchUpExercise({schedule, weekIndex}) !== null,
  );
}

/**
 * The zero-based weeks whose exercise a student completed, in any kind. A
 * teach-back is read from their SimTeach puzzle summary
 * (`users/{uid}/{courseId}/simTeachPuzzles`, keyed by puzzle id), which only
 * simteach writes, setting `completedAt` on the first finished attempt. A
 * check is read from `users/{uid}/{courseId}/sectionExercises` (keyed by week),
 * which only the attendance callable writes. So clients cannot forge either.
 *
 * @param params.schedule - The course's section schedule; `undefined` sets no
 *     exercises, so nothing is complete.
 * @param params.teachBackProgress - The puzzle summary map; `undefined` when
 *     the student has none.
 * @param params.exerciseProgress - The exercises map; `undefined` when the
 *     student has none.
 * @return The completed weeks, ascending.
 * @throws Never.
 */
export function getCompletedExerciseWeeks({
  schedule,
  teachBackProgress,
  exerciseProgress,
}: {
  schedule: SectionScheduleDocument | undefined;
  teachBackProgress:
    | Readonly<Record<string, {completedAt?: unknown} | undefined>>
    | undefined;
  exerciseProgress:
    | Readonly<Record<string, {completedAt?: unknown} | undefined>>
    | undefined;
}): number[] {
  const isSet = (value: unknown) => value !== undefined && value !== null;
  return getExerciseWeeks(schedule).filter(weekIndex => {
    const puzzleId = getTeachBackPuzzleId({schedule, weekIndex});
    const teachBackDone =
      puzzleId !== null &&
      teachBackProgress !== undefined &&
      Object.hasOwn(teachBackProgress, puzzleId) &&
      isSet(teachBackProgress[puzzleId]?.completedAt);
    const key = String(weekIndex);
    const checkDone =
      exerciseProgress !== undefined &&
      Object.hasOwn(exerciseProgress, key) &&
      isSet(exerciseProgress[key]?.completedAt);
    return teachBackDone || checkDone;
  });
}

/** Narrows a Firestore value to an object before reading its fields. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The boolean entries of a per-week document; anything else is dropped. */
function readWeekBooleans(data: unknown): Record<string, boolean> {
  if (!isRecord(data)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(data).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
    ),
  );
}

/**
 * The zero-based weeks whose live Teach it back (#5270) the student was
 * credited for: the 1-based week keys of their summary
 * (`users/{uid}/{courseId}/teachBack`, which only the server writes) whose
 * `creditedAttemptId` is set. A week whose Teach it back the student has not
 * passed is not returned, so this never takes credit away: a Super Section
 * join in a week the schedule sets no exercise for still counts on its own.
 *
 * @param summary - The summary document; anything malformed reads as empty.
 * @return The weeks, ascending.
 * @throws Never.
 */
export function getCreditedTeachBackWeeks(summary: unknown): number[] {
  if (!isRecord(summary)) {
    return [];
  }
  return Object.entries(summary)
    .flatMap(([key, entry]) => {
      const sectionWeek = parseWeekKey(key);
      if (sectionWeek === null || sectionWeek < 1 || !isRecord(entry)) {
        return [];
      }
      const creditedAttemptId = entry.creditedAttemptId;
      return typeof creditedAttemptId === 'string' && creditedAttemptId !== ''
        ? [sectionWeek - 1]
        : [];
    })
    .sort((a, b) => a - b);
}

/** The sorted union of two week lists. */
function unionWeeks(a: readonly number[], b: readonly number[]): number[] {
  return [...new Set([...a, ...b])].sort((x, y) => x - y);
}

/** The section-id lists of a per-week document; anything else is dropped. */
function readWeekIds(data: unknown): Record<string, string[]> {
  if (!isRecord(data)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(data).flatMap(([key, ids]) =>
      Array.isArray(ids)
        ? [[key, ids.filter((id): id is string => typeof id === 'string')]]
        : [],
    ),
  );
}

/**
 * Builds the input to {@link computeSectionWeekCredit} from the raw Firestore
 * documents, so the functions and the webapp read them the same way. A
 * missing or malformed document reads as empty; a malformed schedule sets no
 * exercises.
 *
 * @param docs.sectionAttendance - `users/{uid}/{courseId}/sectionAttendance`.
 * @param docs.sectionAttendanceIds - `.../sectionAttendanceIds`.
 * @param docs.sectionRecordingAttendance - `.../sectionRecordingAttendance`.
 * @param docs.sectionRecordingAttendanceIds -
 *     `.../sectionRecordingAttendanceIds`.
 * @param docs.superSectionIds - The course document's `superSectionIds`.
 * @param docs.schedule - `course/{courseId}/sectionSchedule/schedule`.
 * @param docs.teachBackProgress - `users/{uid}/{courseId}/simTeachPuzzles`.
 * @param docs.exerciseProgress - `users/{uid}/{courseId}/sectionExercises`.
 *     Omitted: no checks completed.
 * @param docs.teachBackSummary - `users/{uid}/{courseId}/teachBack`, the live
 *     Teach it back summary. Omitted: no live Teach it back credited.
 * @return The normalized input.
 * @throws Never.
 */
export function readSectionWeekCreditInput(docs: {
  sectionAttendance: unknown;
  sectionAttendanceIds: unknown;
  sectionRecordingAttendance: unknown;
  sectionRecordingAttendanceIds: unknown;
  superSectionIds: unknown;
  schedule: unknown;
  teachBackProgress: unknown;
  exerciseProgress?: unknown;
  teachBackSummary?: unknown;
}): SectionWeekCreditInput {
  const parsedSchedule = SectionScheduleDocumentSchema.safeParse(
    docs.schedule ?? {},
  );
  const schedule = parsedSchedule.success ? parsedSchedule.data : undefined;
  const readCompletions = (data: unknown) =>
    isRecord(data)
      ? Object.fromEntries(
          Object.entries(data).flatMap(([key, entry]) =>
            isRecord(entry) ? [[key, {completedAt: entry.completedAt}]] : [],
          ),
        )
      : undefined;
  const teachBackProgress = readCompletions(docs.teachBackProgress);
  const exerciseProgress = readCompletions(docs.exerciseProgress);
  const creditedTeachBackWeeks = getCreditedTeachBackWeeks(
    docs.teachBackSummary,
  );
  return {
    sectionAttendance: readWeekBooleans(docs.sectionAttendance),
    sectionAttendanceIds: readWeekIds(docs.sectionAttendanceIds),
    sectionRecordingAttendance: readWeekBooleans(
      docs.sectionRecordingAttendance,
    ),
    sectionRecordingAttendanceIds: readWeekIds(
      docs.sectionRecordingAttendanceIds,
    ),
    superSectionIds: Array.isArray(docs.superSectionIds)
      ? docs.superSectionIds.filter(
          (id): id is string => typeof id === 'string',
        )
      : [],
    exerciseWeeks: unionWeeks(
      getExerciseWeeks(schedule),
      creditedTeachBackWeeks,
    ),
    completedExerciseWeeks: unionWeeks(
      getCompletedExerciseWeeks({
        schedule,
        teachBackProgress,
        exerciseProgress,
      }),
      creditedTeachBackWeeks,
    ),
  };
}

/**
 * Decides which section weeks count toward a student's certificate. A week
 * counts when any of these holds, checked in order:
 * 1. Live attendance in a small section, a makeup, or a staff entry (any
 *    attended section id that is not a Super Section, or no ids at all).
 * 2. A Super Section join: with the week's exercise when the schedule sets
 *    one, on its own when it does not.
 * 3. A Super Section recording marked watched, plus the exercise.
 * 4. A small-section recording marked watched, plus the exercise, for the
 *    earliest {@link MAX_CATCH_UP_WEEKS} such weeks.
 * Each week is judged on that week's records alone, so a student who moved
 * between a Super Section and a small section keeps both kinds of weeks.
 *
 * @param input - The student's per-week records; see
 *     {@link SectionWeekCreditInput}.
 * @return The credited attendance map, each week's standing, and the
 *     catch-ups used and left.
 * @throws Never.
 */
export function computeSectionWeekCredit(
  input: SectionWeekCreditInput,
): SectionWeekCredit {
  const superSectionIds = new Set(input.superSectionIds);
  const exerciseWeeks = new Set(input.exerciseWeeks);
  const completedExerciseWeeks = new Set(
    input.completedExerciseWeeks.filter(week => exerciseWeeks.has(week)),
  );

  const creditedAttendance: Record<string, boolean> = {};
  const weekIndices = new Set<number>(completedExerciseWeeks);
  for (const [key, attended] of Object.entries(input.sectionAttendance)) {
    creditedAttendance[key] = false;
    const weekIndex = parseWeekKey(key);
    if (weekIndex === null) {
      creditedAttendance[key] = attended === true;
      continue;
    }
    weekIndices.add(weekIndex);
  }
  for (const key of Object.keys(input.sectionRecordingAttendance)) {
    const weekIndex = parseWeekKey(key);
    if (weekIndex !== null) {
      weekIndices.add(weekIndex);
    }
  }

  const weeks: SectionWeekCreditEntry[] = [];
  const catchUpWeeks: number[] = [];
  for (const weekIndex of [...weekIndices].sort((a, b) => a - b)) {
    const key = String(weekIndex);
    const isExerciseSet = exerciseWeeks.has(weekIndex);
    const isExerciseDone = completedExerciseWeeks.has(weekIndex);

    const attendedIds = input.sectionAttendanceIds[key] ?? [];
    const attendedLive = input.sectionAttendance[key] === true;
    const isLiveVerified =
      attendedLive &&
      (attendedIds.length === 0 ||
        attendedIds.some(sectionId => !superSectionIds.has(sectionId)));
    const isLiveSuperSection = attendedLive && !isLiveVerified;

    const recordingIds = input.sectionRecordingAttendanceIds[key] ?? [];
    const markedRecording = input.sectionRecordingAttendance[key] === true;
    const isSuperSectionRecording =
      markedRecording &&
      (recordingIds.length === 0 ||
        recordingIds.some(sectionId => superSectionIds.has(sectionId)));

    let status: SectionWeekCreditStatus;
    if (isLiveVerified) {
      status = 'live';
    } else if (isLiveSuperSection && !isExerciseSet) {
      status = 'superSectionLive';
    } else if (
      isExerciseDone &&
      (isLiveSuperSection || isSuperSectionRecording)
    ) {
      status = 'superSection';
    } else if (isExerciseDone && markedRecording) {
      if (catchUpWeeks.length < MAX_CATCH_UP_WEEKS) {
        catchUpWeeks.push(weekIndex);
        status = 'catchUp';
      } else {
        status = 'overCatchUpLimit';
      }
    } else if (isLiveSuperSection || (markedRecording && isExerciseSet)) {
      status = 'needsExercise';
    } else if (markedRecording) {
      status = 'noExercise';
    } else if (isExerciseDone) {
      status = 'needsRecording';
    } else {
      status = 'absent';
    }

    const isCredited = CREDITED_STATUSES.has(status);
    if (isCredited || Object.hasOwn(creditedAttendance, key)) {
      creditedAttendance[key] = isCredited;
    }
    weeks.push({weekIndex, status, isCredited});
  }

  return {
    creditedAttendance,
    weeks,
    catchUpWeeks,
    catchUpsRemaining: MAX_CATCH_UP_WEEKS - catchUpWeeks.length,
  };
}
